import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, Clock, AtSign, Facebook, Instagram, Linkedin } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { CONTACT } from "../lib/contact";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DisclosureBanner from "../components/DisclosureBanner";

export default function Contact() {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <DisclosureBanner />
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Contact</div>
            <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white display">Get in touch.</h1>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-lg">Questions about hiring, our service area, or general inquiries — use the form or reach us directly.</p>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Phone</div>
                  <a data-testid="contact-phone" href={`tel:${CONTACT.phoneRaw}`} className="text-white font-semibold hover:text-amber-400 text-lg">{CONTACT.phone}</a>
                  <div className="text-sm text-slate-400">Call or text · {CONTACT.hours}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Email</div>
                  <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="text-white font-semibold hover:text-amber-400 break-all">{CONTACT.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Location</div>
                  <div data-testid="contact-location" className="text-white font-semibold">{CONTACT.location}</div>
                  <div className="text-sm text-slate-400">Serving Baltimore City · Baltimore County · Anne Arundel · Howard</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Hours</div>
                  <div data-testid="contact-hours" className="text-white font-semibold">{CONTACT.hours}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0"><AtSign className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Follow us · @{CONTACT.handle}</div>
                  <div className="mt-1 flex items-center gap-2">
                    {[
                      { id: "facebook", Icon: Facebook, href: CONTACT.facebook },
                      { id: "instagram", Icon: Instagram, href: CONTACT.instagram },
                      { id: "linkedin", Icon: Linkedin, href: CONTACT.linkedin },
                    ].map((s) => (
                      <a key={s.id} data-testid={`contact-social-${s.id}`} href={s.href} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-300 hover:text-amber-400 hover:border-amber-500/60 transition-colors">
                        <s.Icon className="w-4 h-4" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8 space-y-5">
            <div>
              <Label>Name <span className="text-amber-400">*</span></Label>
              <Input data-testid="contact-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email <span className="text-amber-400">*</span></Label>
                <Input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
              </div>
            </div>
            <div>
              <Label>Subject <span className="text-amber-400">*</span></Label>
              <Input data-testid="contact-subject-input" required value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
            </div>
            <div>
              <Label>Message <span className="text-amber-400">*</span></Label>
              <Textarea data-testid="contact-message-input" required rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
            </div>
            <Button data-testid="contact-submit-btn" type="submit" disabled={sending} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider w-full">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send Message
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
